// @ts-nocheck

import React, {useEffect, useRef, useState} from 'react';
import CellLoader from '@/components/loading/CellLoader';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import useData from '@/utils/useData';
import {UserPreferences} from '@/views/settings/stores/user_preferences';
import {buildURL} from '@/utils/urlBuilder';
import WaveSurfer from 'wavesurfer.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import axios from 'axios';
import i18n from 'i18n';

export interface Props {
    eventId: number;
    timelineBlock: any;
    timelineBlockWords: any;
    isMono: boolean;
    onWaveSurferReady: any;
    autoScrollScope?: 'event' | 'search' | 'textSamples';
}

const AudioPlayer = React.memo<Props>(({
    eventId,
    timelineBlock,
    timelineBlockWords,
    isMono,
    onWaveSurferReady,
    autoScrollScope = 'event',
}) => {

    const [refresh, setRefresh] = useState(false);
    const [isAudioLoading, setIsAudioLoading] = useState(true);
    const waveSurfer = useRef(null);
    let audioFileUrl = buildURL('getCallAudioListen', {event_id: eventId});
    audioFileUrl += `?timestamp=${new Date().getTime()}`;
    const [preferences, loadingPreferences] = useData<UserPreferences>(window.URLS.userPreferencesResource, {
        useLocalStore: true,
        raiseErrors: false,
    });

    const getAutoScrollValue = (): boolean => {
        if (autoScrollScope === 'search') {
            return Boolean(preferences?.autoScrollSearchView);
        }
        if (autoScrollScope === 'textSamples') {
            return Boolean(preferences?.autoScrollTextSamples);
        }
        return Boolean(preferences?.autoScrollEventView);
    };

    const refreshFunction = () => {
        document.getElementById('no-anon-audio-available-disclaimer')?.classList.add('hidden');
        document.getElementById('spinner-loader')?.classList.remove('hidden');
        setIsAudioLoading(true);
        setRefresh(prevRefresh => !prevRefresh);
    };

    const playPause = () => {
        if (waveSurfer.current) {
            const playButtonIcon = document.getElementById('play-button-icon');
            if (waveSurfer.current?.isPlaying()) {
                playButtonIcon?.classList.remove('mdi-pause-circle');
                playButtonIcon?.classList.add('mdi-play-circle');
            } else {
                playButtonIcon?.classList.remove('mdi-play-circle');
                playButtonIcon?.classList.add('mdi-pause-circle');
            }
            waveSurfer.current?.playPause();
        }
    };

    const changePlayerSpeed = (t) => {
        waveSurfer.current?.setPlaybackRate(t)
        Array.from(document.getElementsByClassName('speed-icon')).forEach(el => {
            el.classList.remove('active')
        })
        Array.from(document.getElementsByClassName('speed-icon')).forEach(el => {
            if (el.value == t) {el.classList.add('active')}
        })
    };

    const scrollToTimelineElement = () => {
        if (waveSurfer.current) {
            const currentTime = waveSurfer.current?.getCurrentTime();

            if (timelineBlock) {
                for (const [elementId, phrase] of timelineBlock.entries()) {
                    if (currentTime > phrase.startTime) {
                        if (currentTime < phrase.endTime) {
                            if (document.getElementById('auto-scroll-btn')?.classList.contains('active')) {
                                const timelineBlockEl = document.getElementById('timeline-block');
                                const scrollBlockEl = document.getElementById('Scroll-block-' + elementId);
                                if (timelineBlockEl && scrollBlockEl) {
                                    timelineBlockEl.scrollTop = scrollBlockEl.offsetTop - 420;
                                }
                            }
                            if (
                                !document
                                    .getElementById('Scroll-block-' + elementId)?.classList.contains('active')
                            ) {
                                document
                                    .getElementById('Scroll-block-' + elementId)?.classList.add('active');
                                setTimeout(function () {
                                    document
                                        .getElementById('Scroll-block-' + elementId)?.classList.remove('active');
                                    setTimeout(function () {
                                        document
                                            .getElementById('Scroll-block-' + elementId)?.classList.remove('active');
                                    }, 5000);
                                }, 5000);
                            }
                        }
                    }
                }
            }

            if (timelineBlockWords) {
                timelineBlockWords.forEach(words => { // TODO: timelineBlockWords variable doesn't update, needs fixing
                    words.forEach(word => {
                        if (currentTime > word.startTime && currentTime < word.endTime) {
                            const wordEl = document.getElementById('timelineBlock-word-' + word.word_id)
                            if (
                                !wordEl?.classList.contains('word-active')
                            ) {
                                wordEl?.classList.add('word-active');
                                setTimeout(function () {
                                    wordEl?.classList.remove('word-active');
                                    setTimeout(function () {
                                        wordEl?.classList.remove('word-active');
                                    }, 2000);
                                }, 1000);
                            }
                        }
                    })
                })
            }

        }
    }

    useEffect(() => {
        if (preferences) {
            const hidePlayerButtons = (id) => {
                document.getElementById('spinner-loader')?.classList.add('hidden');
                document.getElementById('play-button')?.classList.add('hidden')
                Array.from(document.getElementsByClassName('speed-icon')).forEach(el => el.classList.add('hidden'))
                document.getElementById(id)?.classList.remove('hidden')
                setIsAudioLoading(false);
            }

            const getAudioBlob = async () => {

                window.AudioContext = window.AudioContext || window.webkitAudioContext;
                const audioCtx = new AudioContext();

                if (!waveSurfer.current) {
                    const wvSurf = WaveSurfer.create({
                        height: 50,
                        barWidth: 3,
                        barHeight: 2,
                        container: '#waveform',
                        waveColor: '#009efb',
                        progressColor: '#0082ce',
                        splitChannels: true,
                        normalize: true,
                        responsive: true,
                        // backend: 'MediaElement', // TODO: fixes pitchshifting issue, disables scrolling
                        plugins: [
                            TimelinePlugin.create({
                                container: '#waveform-timeline',
                            }),
                        ],
                        audioContext: audioCtx,
                    });
                    waveSurfer.current = wvSurf;
                    window.ws = waveSurfer.current;
                }

                axios.options(audioFileUrl).then(resp => {
                    const {
                        file_exists,
                        transcription_completed,
                        anonymized_transcription,
                        anonymized_file_exists,
                        anonymized_file_status
                    } = resp.data;

                    // Case: Masking on is set
                    if (preferences?.dataAnonymized) {

                        // Case 1: Transcription is not completed → Cannot proceed with anonymization
                        if (!transcription_completed) {
                            hidePlayerButtons('no-trans-available-disclaimer');
                            return;
                        }

                        // Case 2: No anonymized transcription available → Anonymization is not possible
                        if (!anonymized_transcription) {
                            hidePlayerButtons('no-trans-available-disclaimer');
                            return;
                        }

                        // Case 3: No anonymized file exists → Check anonymization task status
                        if (!anonymized_file_exists) {
                            // Case 3a: If task is in progress (not ERROR), show "Anonymization started" message
                            if (anonymized_file_status !== 'ERROR') {
                                waveSurfer.current.load(audioFileUrl);
                                hidePlayerButtons('anon-process-started-disclaimer');
                            }
                            // Case 3b: If task has failed, show "Anonymization error" message
                            else {
                                hidePlayerButtons('error-anon-audio-disclaimer');
                            }

                            return;
                        }

                        // Case 4: Anonymized file exists → Check its status
                        switch (anonymized_file_status) {
                            // Case 4a: Anonymization completed → Load the anonymized audio
                            case 'COMPLETED':
                                document.getElementById('no-anon-audio-available-disclaimer')?.classList.add('hidden');
                                document.getElementById('anon-process-started-disclaimer')?.classList.add('hidden');
                                waveSurfer.current.load(audioFileUrl);
                                break;

                            // Case 4b: Anonymization failed → Show error message
                            case 'ERROR':
                                hidePlayerButtons('error-anon-audio-disclaimer');
                                break;

                            // Case 4c: Anonymization process is still running → Show waiting message
                            default:
                                hidePlayerButtons('anon-process-started-disclaimer');
                                break;
                        }

                        return;
                    }

                    // Case: Masking off is set
                    else {
                        // Case 5: Regular (non-anonymized) audio file exists → Load it
                        if (file_exists) {
                            waveSurfer.current.load(audioFileUrl);
                        }
                        // Case 6: No audio available at all → Show "No audio available" message
                        else {
                            hidePlayerButtons('no-audio-available-disclaimer');
                        }
                    }
                }).catch(error => {
                    // Case 7: API request failed → Show "No audio available" message
                    console.error(error);
                    hidePlayerButtons('no-audio-available-disclaimer');
                });


            };
            getAudioBlob();

            if (waveSurfer.current) {
                if (document.getElementById('modal_' + eventId)) {
                    var playerWatchLinkChange = setInterval(() => {
                        if (!document.getElementById('modal_' + eventId)) {
                            if (waveSurfer.current?.isPlaying()) {playPause();}
                            clearInterval(playerWatchLinkChange)
                        }
                    }, 500)
                } else {
                    var playerWatchLinkChange = setInterval(() => {
                        if ((!window.location.href.includes(`events/event/${eventId}`))) {
                            if (waveSurfer.current?.isPlaying()) {playPause();}
                            clearInterval(playerWatchLinkChange)
                        }
                    }, 500)
                }


                waveSurfer.current.on('audioprocess', function () {
                    if (waveSurfer.current.isPlaying()) {
                        scrollToTimelineElement();
                    } else if (Math.round(waveSurfer.current.getCurrentTime() * 10) === Math.round(waveSurfer.current.getDuration() * 10 - .1)) {
                        playPause();
                    }
                });

                waveSurfer.current.on('ready', function () {
                    document.getElementById('spinner-loader')?.classList.add('hidden');
                    document.getElementById('play-button')?.classList.remove('hidden');
                    Array.from(document.getElementsByClassName('waveform-container')).forEach(el => el.classList.toggle('hidden'))
                    Array.from(document.getElementsByClassName('speed-icon')).forEach(el => el.classList.remove('hidden'));
                    setIsAudioLoading(false);
                    document.getElementById('auto-scroll-btn')?.classList.toggle('active', getAutoScrollValue());
                    if (onWaveSurferReady) {
                        onWaveSurferReady(waveSurfer.current);
                    }
                });

                waveSurfer.current.on('seek', function () {
                    scrollToTimelineElement();
                });

                waveSurfer.current.on('finish', function () {
                    document.getElementById('play-button-icon')?.classList.remove('mdi-pause-circle');
                    document.getElementById('play-button-icon')?.classList.add('mdi-play-circle');
                    waveSurfer.current.stop();
                });
            }
        }
    }, [preferences, refresh])


    return (
        <>
            <div className='text-center hidden mt-5' id='no-audio-available-disclaimer'>
                <h4>{i18n.t('jt_audio_available.text')}</h4>
            </div>
            <div className='text-center hidden mt-5' id='no-anon-audio-available-disclaimer'>
                <h4>N{i18n.t('jt_anonymised_audio.text')}</h4>
            </div>
            <div className='text-center hidden mt-5' id='no-trans-available-disclaimer'>
                <h4>{i18n.t('jt_audio_cannot.text')}</h4>
            </div>
            <div className='text-center hidden mt-5' id='anon-process-started-disclaimer'>
                <h4>{i18n.t('jt_audio_anonymisation_process_started.text')}</h4>
                <Button variant="info" icon="mdi-refresh" onClick={refreshFunction} style={{marginTop: '12px'}} data-cy="audio-player-refresh-info-btn">
                    {i18n.t('jt_refresh.text')}
                </Button>
            </div>
            <div className='text-center hidden mt-5' id='error-anon-audio-disclaimer'>
                <h4>{i18n.t('jt_audio_anonymisation_process_failed.text')}</h4>
            </div>
            {!loadingPreferences && isAudioLoading && (
                <CellLoader
                    text={preferences?.dataAnonymized ? i18n.t('jt_loading_anonymized_audio.text') : i18n.t('jt_loading_audio.text')}
                    fullHeight={false}
                />
            )}

            <div className="d-flex justify-content-between">
                <div className={'waveform py-3 mr-3 w-100'}>
                    <div id='waveform_counter' />
                    <div id='waveform' />
                    <div id='waveform-timeline' />
                    <Tooltip
                        content={i18n.t('jt_play_pause.text')}
                        placement='bottom'
                    >
                        <button
                            className={
                                `nav-item m-1 btn btn-info player-icon hidden
                                                ${preferences?.dataAnonymized ? 'anonymized' : ''}
                                                ${isMono ? 'mono' : ''}
                                                ${(preferences?.dataAnonymized && isMono) ? 'anonymized-mono' : ''}`
                            }
                            id='play-button'
                            key='play-button'
                            onClick={() => {
                                playPause()
                            }}
                        >
                            <span id='play-button-icon'
                                className={'text-xl mdi mdi-play-circle'}></span>
                        </button>
                    </Tooltip>
                </div>

                <div className='py-3 d-flex flex-column justify-content-between'>
                    <Tooltip
                        content={i18n.t('tt_change_speed_to_0_75x_of_original.text')}
                        placement='bottom'
                    >
                        <Button
                            variant="info"
                            className="nav-item speed-icon hidden mb-1"
                            size='sm'
                            onClick={() => {
                                changePlayerSpeed(0.75)
                            }}
                        >
                            {i18n.t('jt_text.text')}
                        </Button>
                    </Tooltip>


                    <Tooltip
                        content={i18n.t('jt_original_speed.text')}
                        placement='bottom'
                    >
                        <Button
                            variant="info"
                            size='sm'
                            className="nav-item speed-icon active hidden mb-1"
                            onClick={() => {
                                changePlayerSpeed(1)
                            }}
                        >
                            {i18n.t('jt_text_speed_1x.text')}
                        </Button>
                    </Tooltip>


                    <Tooltip
                        content={i18n.t('tt_change_speed_to_1_25x_of_original.text')}
                        placement='bottom'
                    >
                        <Button
                            variant="info"
                            size='sm'
                            className="nav-item speed-icon hidden mb-1"
                            onClick={() => {
                                changePlayerSpeed(1.25)
                            }}
                        >
                            {i18n.t('jt_text_speed_1_25x.text')}
                        </Button>
                    </Tooltip>
                </div>
            </div>
        </>
    )
});

export default AudioPlayer;